import React, { useContext, useEffect, useReducer, useState } from 'react';
import api from '../api';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { Store } from '../Store';
import { useNavigate, useParams } from 'react-router-dom';
import { getError, formatEuro, formatDate } from '../utils';
import { totalAfterRefund } from '../utils/orderTotals';
import { isSameAddress } from '../utils/checkout';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import Card from 'react-bootstrap/Card';
import { useTranslation } from 'react-i18next';
import { Container, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

const reducer = (state, action) => {
  // reducer function to update the state for different cases
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, order: action.payload, error: '' };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function OrderScreen() {
  const [{ loading, error, order }, dispatch] = useReducer(reducer, {
    // default values:
    loading: true,
    order: {},
    error: '',
  }); // useReducer function instead of useState hook, to manage complex state. Gets the states (loading, oder and error) and dispatch, to dispatch the actions from the reducer above

  const { t } = useTranslation();
  const { state } = useContext(Store); // useContext gives access to the state of the context (in this case the Store)
  const { userInfo } = state; // gets the userInfo out of the application state

  const params = useParams(); // extracts the URL parameters from the current route
  const { orderId } = params; // uses ES6 object destructuring to extract the id property from params

  const navigate = useNavigate(); // enables the functionality, to navigate a user to a different screen

  // Net/VAT breakdown is disabled while no VAT is charged — uncomment together with the two blocks further down when VAT applies again:
  // const [showAdditionalItems, setShowAdditionalItems] = useState(false);
  const [show, setShow] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState(''); // Store the thumbnail URL

  const shippingSameAsBilling = isSameAddress(
    order?.shippingAddress,
    order?.billingAddress
  ); // the checkout's rule (utils/checkout.js), over the same nine keys the screen listed until chain 10 (Oct 2026)

  const handleShow = (thumbnail) => {
    setSelectedThumbnail(thumbnail); // Update selected thumbnail
    setShow(true); // Show modal
  };

  const handleClose = () => setShow(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await api.get(`/api/orders/${orderId}`); // fetches the order, based on the orderId, from the backend

        // Fetch signed URLs for thumbnails
        const designIds = data.orderItems.map((item) => item.designId);
        const { data: signedUrls } = await api.post('/api/designs/thumbnails', {
          designIds,
        });

        // Merge signed URLs into order items
        const updatedOrderItems = data.orderItems.map((item) => ({
          ...item,
          signedThumbnailUrl:
            signedUrls.thumbnails?.[item.designId] || item.thumbnail,
        }));

        // Dispatch updated order data
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: { ...data, orderItems: updatedOrderItems },
        });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: t(getError(err)) });
      }
    };

    if (!userInfo) {
      return navigate('/login');
    }
    if (!order.orderId || (order.orderId && order.orderId !== orderId)) {
      fetchOrder(); // if the order doesnt exist (hasn't been fetched yet), or the order.orderId doesnt match the current orderId, then the order will be fetched from the backend
    }
  }, [order, userInfo, orderId, navigate]);

  // Disabled with the net/VAT breakdown above — if the Order Total <ListGroup.Item> is clicked, two more ListGroup.Items show up, displaying the totalNet and totalVat:
  // const toggleAdditionalItems = () => {
  //   setShowAdditionalItems(!showAdditionalItems);
  // };

  const invoiceCorrections =
    order.invoices?.filter((inv) =>
      ['cancellation', 'correction'].includes(inv.type)
    ) || [];

  const downloadInvoice = async (invoiceId) => {
    try {
      const response = await api.get(
        `/api/downloads/invoice/${order.orderId}/${invoiceId}`,
        {
          responseType: 'blob', // Ensure the response is treated as binary data
        }
      );

      // Create a Blob and generate a URL for the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);

      // Open the PDF in a new browser tab
      window.open(pdfUrl, '_blank');

      // Revoke the URL after opening to clean up resources
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000); // Delay to ensure PDF loads
    } catch (error) {
      toast.error(t(getError(error)));
    }
  };

  return (
    <Container className="order-screen-container">
      <title>{t('order-screen-helmet')}</title>

      <h1 className="screen-heading">{t('order-screen-heading')}</h1>

      {loading ? (
        <div className="overlay">
          <LoadingLogo />
        </div>
      ) : error ? (
        <MessageBox className="danger-alert">{error}</MessageBox>
      ) : (
        <div className="order-summary-container">
          <Modal
            show={show}
            onHide={handleClose}
            centered
            className="thumbnail-modal"
          >
            <Modal.Body>
              <img
                src={selectedThumbnail}
                alt={t('order-screen-selected-thumbnail')}
              />
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                {t('order-screen-close')}
              </Button>
            </Modal.Footer>
          </Modal>

          <div className="address-payment-items-container">
            {/* The following Card is displayed for screens <= 991px */}
            <Card className="d-lg-none order-card">
              <Card.Body>
                <div className="order-card-order-information">
                  <div className="order-card-order-id">
                    <strong>{t('order-screen-order-id')}</strong>
                    <br />
                    {orderId}
                  </div>

                  <div className="order-card-order-date">
                    <strong>{t('order-screen-order-date')}</strong>
                    <br />
                    {formatDate(order.orderDate)}
                  </div>
                </div>

                {order.status === 'shipped' &&
                  order.trackingNumbers?.length > 0 &&
                  (() => {
                    const tn = order.trackingNumbers[0];
                    const providerKey = (tn.provider || 'DHL').toUpperCase();
                    const number = tn.number;

                    const providerLabelByKey = {
                      DHL: 'DHL',
                      UPS: 'UPS',
                      DPD: 'DPD',
                      FEDEX: 'FedEx',
                    };

                    const trackingUrlByProvider = {
                      DHL: (n) =>
                        `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(
                          n
                        )}&brand=DHL`,
                      UPS: (n) =>
                        `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(
                          n
                        )}`,
                      DPD: (n) =>
                        `https://www.dpd.com/tracking/?parcelNumber=${encodeURIComponent(
                          n
                        )}`,
                      FEDEX: (n) =>
                        `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(
                          n
                        )}`,
                    };

                    const providerLabel =
                      providerLabelByKey[providerKey] || tn.provider || 'DHL';
                    const trackingUrl =
                      trackingUrlByProvider[providerKey]?.(number) ||
                      trackingUrlByProvider.DHL(number);

                    return (
                      <div className="order-card-tracking-number-container">
                        <strong>
                          {providerLabel} {t('order-screen-tracking-number')}
                        </strong>
                        <br />

                        <div className="order-card-tracking-number">
                          <a
                            href={trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {/* optional: show provider label */}
                            {/* {provider}: {number} */}
                            {number}
                          </a>

                          <Button
                            variant="light"
                            onClick={() => {
                              navigator.clipboard.writeText(number);
                              toast.success(
                                t('order-screen-tracking-number-copied')
                              );
                            }}
                          >
                            {t('order-screen-copy')}
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
              </Card.Body>
            </Card>

            <Card className="order-card">
              <Card.Body>
                <Card.Title>
                  {t(
                    shippingSameAsBilling
                      ? 'order-screen-shipping-and-billing-address'
                      : 'order-screen-shipping-address'
                  )}
                </Card.Title>

                <div className="divider"></div>

                <Card.Text>
                  {order.shippingAddress.companyName ? (
                    <>
                      <span>{order.shippingAddress.companyName}</span>
                      <br />
                    </>
                  ) : null}
                  <span>{order.shippingAddress.fullName}</span>
                  <br />
                  {/* email and phone are rendered conditionally, because these fields are not included inside old orders:  */}
                  {order.shippingAddress.email ? (
                    <>
                      <span>{order.shippingAddress.email}</span>
                      <br />
                    </>
                  ) : null}
                  {order.shippingAddress.phone ? (
                    <>
                      <span>{order.shippingAddress.phone}</span>
                      <br />
                    </>
                  ) : null}
                  <span>
                    {order.shippingAddress.street +
                      ' ' +
                      order.shippingAddress.houseNumber}
                    , {order.shippingAddress.postalCode}{' '}
                    {order.shippingAddress.city},{' '}
                    {order.shippingAddress.country}
                  </span>
                </Card.Text>
              </Card.Body>
            </Card>

            {/** Billing Address (only shown if different from shipping) **/}
            {!shippingSameAsBilling && (
              <Card className="order-card">
                <Card.Body>
                  <Card.Title>{t('order-screen-billing-address')}</Card.Title>

                  <div className="divider"></div>

                  <Card.Text>
                    {order.billingAddress.companyName ? (
                      <>
                        <span>{order.billingAddress.companyName}</span>
                        <br />
                      </>
                    ) : null}
                    <span>{order.billingAddress.fullName}</span>
                    <br />
                    {/* email and phone are rendered conditionally, because these fields are not included inside old orders:  */}
                    {order.billingAddress.email ? (
                      <>
                        <span>{order.billingAddress.email}</span>
                        <br />
                      </>
                    ) : null}
                    {order.billingAddress.phone ? (
                      <>
                        <span>{order.billingAddress.phone}</span>
                        <br />
                      </>
                    ) : null}
                    <span>
                      {order.billingAddress.street +
                        ' ' +
                        order.billingAddress.houseNumber}
                      , {order.billingAddress.postalCode}{' '}
                      {order.billingAddress.city},{' '}
                      {order.billingAddress.country}
                    </span>
                  </Card.Text>
                </Card.Body>
              </Card>
            )}

            <Card className="order-card">
              <Card.Body>
                <Card.Title>{t('order-screen-payment')}</Card.Title>

                <div className="divider"></div>

                {order.paymentMethod === 'paypal' ? (
                  <div>
                    <img
                      className="order-card-paypal-img"
                      src="https://custom-keycaps-images.s3.eu-central-1.amazonaws.com/PayPal.png"
                      alt="PayPal"
                    />
                  </div>
                ) : order.paymentMethod === 'card' ? (
                  <div>
                    <img
                      className="order-card-master-img"
                      src="https://custom-keycaps-images.s3.eu-central-1.amazonaws.com/Mastercard.png"
                      alt="Mastercard"
                    />
                    <img
                      className="order-card-visa-img"
                      src="https://custom-keycaps-images.s3.eu-central-1.amazonaws.com/Visa.png"
                      alt="Visa"
                    />
                    <img
                      className="order-card-amex-img"
                      src="https://custom-keycaps-images.s3.eu-central-1.amazonaws.com/AmericanExpress.png"
                      alt="AmericanExpress"
                    />
                  </div>
                ) : (
                  <Card.Text>{order.paymentMethod}</Card.Text>
                )}
              </Card.Body>
            </Card>

            <Card className="order-card">
              <Card.Body>
                <Card.Title>{t('order-screen-items')}</Card.Title>

                <div className="d-sm-none divider"></div>

                <ListGroup variant="flush">
                  <ListGroup.Item className="d-none d-sm-flex order-card-items-table-header order-card-table-header-divider">
                    <div className="order-card-items-table-image-heading">
                      {t('order-screen-image')}
                    </div>

                    <div className="order-card-items-table-name-heading">
                      {t('order-screen-name')}
                    </div>

                    <div className="order-card-items-table-quantity-heading">
                      {t('order-screen-quantity')}
                    </div>

                    <div className="order-card-items-table-price-heading">
                      {t('order-screen-price')}
                    </div>
                  </ListGroup.Item>

                  {order.orderItems.map((item, index) => (
                    <div key={item._id}>
                      {index !== 0 && <div className="divider"></div>}

                      <ListGroup.Item
                        key={item._id}
                        className="order-card-items-table-body"
                      >
                        <div className="order-card-items-table-image">
                          {/* the thumbnail sits in a button so the enlarged view is reachable by keyboard (the button takes its accessible name from the image's alt text): */}
                          <button
                            type="button"
                            className="a11y-unstyled-button"
                            onClick={() => handleShow(item.signedThumbnailUrl)} // Show modal on click
                          >
                            <img
                              className="img-fluid img-thumbnail-order-screen"
                              src={item.signedThumbnailUrl}
                              alt={item.name}
                            />
                          </button>
                        </div>

                        <div className="order-card-name-quantity-price-wrapper">
                          <div className="order-card-items-table-name">
                            {item.name}
                          </div>

                          <div className="order-card-items-table-quantity">
                            <span>{t('order-screen-quantity')}:</span>
                            {item.quantity}
                          </div>

                          <div className="order-card-items-table-price">
                            <span>{t('order-screen-price')}:</span>
                            {formatEuro(item.unitGross)}
                            {/* 
                              <div className="incl-vat">
                                {t('order-screen-vat-1', {
                                  vatRate: item.vatRate,
                                })}
                              </div>
                            */}
                          </div>
                        </div>
                      </ListGroup.Item>
                    </div>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </div>

          <div className="order-information-container">
            {/* The following Card is displayed for screens > 991px */}
            <Card className="d-none d-lg-block order-card">
              <Card.Body>
                <div className="order-card-order-information">
                  <div className="order-card-order-id">
                    <strong>{t('order-screen-order-id')}</strong>
                    <br />
                    {orderId}
                  </div>

                  <div className="order-card-order-date">
                    <strong>{t('order-screen-order-date')}</strong>
                    <br />
                    {formatDate(order.orderDate)}
                  </div>
                </div>

                {order.status === 'shipped' &&
                  order.trackingNumbers?.length > 0 &&
                  (() => {
                    const tn = order.trackingNumbers[0];
                    const providerKey = (tn.provider || 'DHL').toUpperCase();
                    const number = tn.number;

                    const providerLabelByKey = {
                      DHL: 'DHL',
                      UPS: 'UPS',
                      DPD: 'DPD',
                      FEDEX: 'FedEx',
                    };

                    const trackingUrlByProvider = {
                      DHL: (n) =>
                        `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(
                          n
                        )}&brand=DHL`,
                      UPS: (n) =>
                        `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(
                          n
                        )}`,
                      DPD: (n) =>
                        `https://www.dpd.com/tracking/?parcelNumber=${encodeURIComponent(
                          n
                        )}`,
                      FEDEX: (n) =>
                        `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(
                          n
                        )}`,
                    };

                    const providerLabel =
                      providerLabelByKey[providerKey] || tn.provider || 'DHL';
                    const trackingUrl =
                      trackingUrlByProvider[providerKey]?.(number) ||
                      trackingUrlByProvider.DHL(number);

                    return (
                      <div className="order-card-tracking-number-container">
                        <strong>
                          {providerLabel} {t('order-screen-tracking-number')}
                        </strong>
                        <br />

                        <div className="order-card-tracking-number">
                          <a
                            href={trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {number}
                          </a>

                          <Button
                            variant="light"
                            onClick={() => {
                              navigator.clipboard.writeText(number);
                              toast.success(
                                t('order-screen-tracking-number-copied')
                              );
                            }}
                          >
                            {t('order-screen-copy')}
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
              </Card.Body>
            </Card>

            <Card className="order-card">
              <Card.Body>
                <div className="order-card-subtotal">
                  <div>{t('order-screen-subtotal')}</div>
                  <div>{formatEuro(order.itemsGross)}</div>
                </div>

                <div className="order-card-shipping">
                  <div>{t('order-screen-shipping')}</div>
                  <div>
                    {order?.shippingGross
                      ? formatEuro(order.shippingGross)
                      : formatEuro(0)}
                  </div>
                </div>

                {typeof order?.refundGross === 'number' &&
                  order.refundGross < 0 && (
                    <div className="order-card-refund">
                      <div>{t('order-screen-refund')}</div>
                      <div>{formatEuro(order.refundGross)}</div>
                    </div>
                  )}

                <div className="order-card-total">
                  <div>{t('order-screen-total')}</div>
                  <div>{formatEuro(totalAfterRefund(order))}</div>
                </div>

                {/* The following part is not responsive yet:
                          <span className="ms-1 incl-vat">
                            {t('order-screen-vat-2')}
                          </span>
                          <Button
                            variant="dark"
                            size="sm"
                            type="button"
                            style={{ marginLeft: '5px' }}
                            onClick={toggleAdditionalItems}
                          >
                            {showAdditionalItems ? (
                              // changes the icon, when the Button is clicked (arrow down icon vs. arrow up icon)
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-chevron-up"
                                viewBox="0 0 16 16"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-chevron-down"
                                viewBox="0 0 16 16"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                                />
                              </svg>
                            )}
                          </Button>
                          */}

                <div className="order-card-no-vat-charged">
                  {t('order-screen-no-vat-charged')}
                </div>

                {/*  The following part is not responsive yet:
                      {showAdditionalItems && (
                        // if showAdditionalItems is true (so when the Order Total ListGroup.Item is clicked), the following ListGroup.Items will be shown
                        <>
                          <ListGroup.Item>
                            <Row>
                              <Col>{t('order-screen-net')}</Col>
                              <Col>{formatEuro(order.totalNet)}</Col>
                            </Row>
                          </ListGroup.Item>
                          <ListGroup.Item>
                            <Row>
                              <Col>{t('order-screen-vat-3')}</Col>
                              <Col>{formatEuro(order.totalVat)}</Col>
                            </Row>
                          </ListGroup.Item>
                        </>
                      )}
                    */}

                <div className="divider"></div>

                <Button
                  variant="secondary"
                  type="button"
                  className="invoice-button"
                  onClick={() => {
                    const originalInvoice = order.invoices?.find(
                      (inv) => inv.type === 'original'
                    ); // find the original invoice from the order's invoices

                    if (!originalInvoice) {
                      toast.error(t('order-screen-original-invoice-not-found'));
                      return;
                    }

                    downloadInvoice(originalInvoice.id);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8.5 6.5a.5.5 0 0 0-1 0v3.793L6.354 9.146a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l2-2a.5.5 0 0 0-.708-.708L8.5 10.293z" />
                    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
                  </svg>

                  {order.invoices?.some(
                    (invoice) =>
                      invoice.type === 'cancellation' ||
                      invoice.type === 'correction'
                  )
                    ? t('order-screen-original-invoice')
                    : t('order-screen-invoice')}
                </Button>

                {invoiceCorrections.map((inv) => (
                  <Button
                    key={inv.id}
                    variant="secondary"
                    type="button"
                    className="invoice-button cancellation-invoice-button"
                    onClick={() => downloadInvoice(inv.id)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8.5 6.5a.5.5 0 0 0-1 0v3.793L6.354 9.146a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l2-2a.5.5 0 0 0-.708-.708L8.5 10.293z" />
                      <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
                    </svg>

                    {t('order-screen-invoice-correction')}
                  </Button>
                ))}
              </Card.Body>
            </Card>
          </div>
        </div>
      )}
    </Container>
  );
}
